import { UserModel } from "@/app/entities";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useToast } from "../toast";
import { displayZodError, ObjectToQueryParams } from "@/utils";
import { EnrolGroupeDataSchema, EnrolUniqueDataSchema } from "./validator";
import useApi from "@/services/use.api";
import { useEnrolementStore } from "@/context/stores/enrolement.store";

const { useGetApi, usePostApi } = useApi();

export type AddEnrolDataModel = {
  type?: string;
  animalType?: string;
  matriculeProprietaire?: string;
  matricule: string;
  sexe: string;
  espece?: string;
  nombrePartiel?: number;
  encrypte?: boolean;
  sousLot: boolean;
  typeEnrolement?: string;
  nom?: string;
  poids?: number;
  taille?: number;
  couleur?: string;
  dateNaissance?: string;
  especeOrigine?: string;
  laitiere?: boolean;
  proprietaire?: UserModel;
};

export interface InfosMarqueurModel {
  campagne: string;
  region: string;
  ville: string;
  lot: string;
  sousLot: string;
  boucleDispoLot: number;
  boucleDispoSousLot: number;
  fabriquePar: string;
  pretPourEnrolement: boolean;
}

export type EnrolCheckModel = {
  animal: any;
  infosMarqueur?: InfosMarqueurModel;
};

export type EnrolUnqiueApiResponse = {
  animalType: string;
  matricule: string;
  guid: string;
  proprietaire: UserModel;
  nom: string;
  poids: number;
  taille: number;
  couleur: string;
  marqueurs: string[];
  evenements: any;
  sexe: string;
  dateNaissance: string;
  dateCreation: string;
  dateEnrolement: string;
  ddm: string;
  image: any;
  type: string;
  especeOrigine: string;
  laitiere: boolean;
};

export const useEnrolement = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const setEnrol = useEnrolementStore((state) => state.setEnrol);

  const saveEnrolement = async (params: AddEnrolDataModel) => {
    const isUniqueEnrol = params.typeEnrolement === "UNIQUE";
    try {
      const validatedData = isUniqueEnrol
        ? EnrolUniqueDataSchema.parse(params)
        : EnrolGroupeDataSchema.parse(params);

      const postData = isUniqueEnrol
        ? { ...validatedData, proprietaire: params.proprietaire, matricule: "" }
        : validatedData;
      setSaving(true);
      const res = await usePostApi<any>(
        isUniqueEnrol
          ? `/auth/common/enrollement/${validatedData.matricule}`
          : `/auth/common/enrollement`,
        postData
      );
      const { data, success, message } = res ?? {};
      setSaving(false);
      if (!success) {
        useToast(message || data?.data || "Une erreur s'est produite", {
          variant: "error",
        });
        return null;
      }
      useToast("Enrolement effectué avec succès.", {
        variant: "success",
      });
      return data as EnrolUnqiueApiResponse;
      // navigate("/dashboard/enrolements");
    } catch (error) {
      setSaving(false);
      displayZodError(error, useToast, {
        defaultErrorMessage: "Erreur de validation",
        showAllErrors: false, // ou true si vous voulez afficher toutes les erreurs
        prefix: "Validation: ", // optionnel
      });
    }
  };

  const verifyMatricule = async (matricule: string): Promise<any> => {
    setLoading(true);
    const param = ObjectToQueryParams({ matricule });
    const res = await usePostApi(`/auth/common/enrollement/${matricule}`, {});
    setLoading(false);
    const { data } = res ?? {};
    return data;
  };

  const checkAnimal = async (matricule: string): Promise<any> => {
    setLoading(true);
    const res = await useGetApi<EnrolCheckModel>(`/public/animal/${matricule}`);
    setLoading(false);
    const { data, success, message } = res;
    if (!success) {
      useToast(
        message ??
          "Une erreur s'est produite lors de la récupération des données",
        { variant: "error" }
      );
      setEnrol(null);
      return null;
    }

    setEnrol(data ?? null);
    return data;
  };

  return {
    loading,
    saving,
    checkAnimal,
    verifyMatricule,
    saveEnrolement,
  };
};
